import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Archive as ArchiveIcon,
  Calendar,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Database,
  FileText,
  Loader2,
  Lock,
  Receipt,
  Star,
  TrendingDown,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEuro = (value) => `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const StatTile = ({ icon: Icon, label, value, accent = 'slate', testid }) => {
  const accents = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100',
  };
  return (
    <div className={`rounded-lg border p-3 ${accents[accent]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-80">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <p className="mt-1 text-lg font-semibold font-outfit" data-testid={`${testid}-value`}>{value}</p>
    </div>
  );
};

const SummaryRow = ({ label, items, accent, empty }) => {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-200 p-3 text-xs text-slate-400 italic">
        {empty}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{label}</p>
      <div className="space-y-1">
        {items.map((it, idx) => (
          <div
            key={`${label}-${idx}`}
            className="flex items-center justify-between text-xs rounded px-2 py-1.5 bg-white border border-slate-100"
            data-testid={`archive-summary-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${idx}`}
          >
            <span className="truncate text-slate-700 mr-2">{it.name || it.method || it.type}</span>
            <span className="text-slate-500 whitespace-nowrap">
              {it.count ? `${it.count}× • ` : ''}
              <span className={`font-semibold ${accent || 'text-slate-900'}`}>{fmtEuro(it.amount)}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ArchivePage = () => {
  const { api } = useAuth();
  const [archives, setArchives] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedYear, setExpandedYear] = useState(null);
  const [detailByYear, setDetailByYear] = useState({});
  const [loadingYear, setLoadingYear] = useState(null);

  const fetchArchives = async () => {
    setListLoading(true);
    try {
      const { data } = await api.get('/archives');
      setArchives(data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du chargement des archives');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchArchives();
  }, []);

  const loadDetail = async (year) => {
    if (detailByYear[year]) return;
    setLoadingYear(year);
    try {
      const { data } = await api.get(`/archives/${year}`);
      setDetailByYear((prev) => ({ ...prev, [year]: data }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du chargement du détail');
    } finally {
      setLoadingYear(null);
    }
  };

  const toggleYear = async (year) => {
    if (expandedYear === year) {
      setExpandedYear(null);
      return;
    }
    setExpandedYear(year);
    await loadDetail(year);
  };

  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  const handleArchiveCurrentYear = async () => {
    setActionLoading(true);
    try {
      await api.post(`/archives/close-year?year=${currentYear}`);
      toast.success(`Snapshot ${currentYear} enregistré`);
      setDetailByYear((prev) => {
        const copy = { ...prev };
        delete copy[currentYear];
        return copy;
      });
      await fetchArchives();
      setExpandedYear(currentYear);
      await loadDetail(currentYear);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur archivage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchivePreviousYear = async () => {
    setActionLoading(true);
    try {
      await api.post(`/archives/close-year?year=${previousYear}`);
      toast.success(`Archive ${previousYear} générée`);
      setDetailByYear((prev) => {
        const copy = { ...prev };
        delete copy[previousYear];
        return copy;
      });
      await fetchArchives();
      setExpandedYear(previousYear);
      await loadDetail(previousYear);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur archivage');
    } finally {
      setActionLoading(false);
    }
  };

  const sortedArchives = useMemo(
    () => [...archives].sort((a, b) => Number(b.year) - Number(a.year)),
    [archives],
  );

  return (
    <div className="space-y-6" data-testid="archive-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3" data-testid="archive-header">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2" data-testid="archive-page-title">
            <ArchiveIcon className="w-6 h-6 text-[#065F46]" />
            Archive
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl" data-testid="archive-page-subtitle">
            Sauvegarde annuelle complète de toutes les données de l’application (RDV, CA, charges, factures, devis, clients). Classée par année, en lecture seule.
          </p>
          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1" data-testid="archive-auto-info">
            <Lock className="w-3 h-3" /> Snapshot automatique de l’année précédente à chaque démarrage • Snapshot manuel possible à tout moment
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleArchivePreviousYear}
            disabled={actionLoading}
            data-testid="archive-snapshot-previous-button"
          >
            {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
            Archiver {previousYear}
          </Button>
          <Button
            type="button"
            className="bg-[#065F46] hover:bg-[#047857] text-white"
            onClick={handleArchiveCurrentYear}
            disabled={actionLoading}
            data-testid="archive-snapshot-current-button"
          >
            <ArchiveIcon className="w-4 h-4 mr-2" />
            Archiver {currentYear}
          </Button>
        </div>
      </div>

      {/* Year cards list */}
      {listLoading ? (
        <div className="py-20 flex items-center justify-center" data-testid="archive-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#065F46]" />
        </div>
      ) : sortedArchives.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200" data-testid="archive-empty">
          <CardContent className="py-14 text-center space-y-3">
            <ArchiveIcon className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="text-slate-600 font-medium">Aucune archive pour le moment</p>
            <p className="text-sm text-slate-400">Cliquez sur « Archiver {currentYear} » pour créer votre premier snapshot annuel.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="archive-years-list">
          {sortedArchives.map((arc, index) => {
            const totals = arc.totals || {};
            const isExpanded = expandedYear === arc.year;
            const detail = detailByYear[arc.year];
            const profit = Number(totals.profit || 0);
            const profitPositive = profit >= 0;

            return (
              <Card
                key={arc.year}
                className="border border-slate-200 overflow-hidden"
                data-testid={`archive-year-card-${index}`}
              >
                {/* Year header (always visible) */}
                <button
                  type="button"
                  onClick={() => toggleYear(arc.year)}
                  className="w-full text-left p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`archive-year-toggle-${index}`}
                >
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold text-slate-900 font-outfit" data-testid={`archive-year-label-${index}`}>
                            {arc.year}
                          </h3>
                          <span
                            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                              arc.mode === 'auto' ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'
                            }`}
                            data-testid={`archive-year-mode-${index}`}
                          >
                            {arc.mode === 'auto' ? 'Auto' : 'Manuel'}
                          </span>
                        </div>
                        {arc.created_at && (
                          <p className="text-[11px] text-slate-400 mt-0.5" data-testid={`archive-year-created-${index}`}>
                            Créé le {new Date(arc.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Quick metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full md:w-auto md:flex md:items-center md:gap-4">
                      <div className="text-right md:text-left" data-testid={`archive-year-revenue-${index}`}>
                        <p className="text-[10px] uppercase text-slate-400">CA</p>
                        <p className="text-sm font-semibold text-slate-900">{fmtEuro(totals.revenue)}</p>
                      </div>
                      <div className="text-right md:text-left" data-testid={`archive-year-charges-${index}`}>
                        <p className="text-[10px] uppercase text-slate-400">Charges</p>
                        <p className="text-sm font-semibold text-slate-900">{fmtEuro(totals.charges)}</p>
                      </div>
                      <div className="text-right md:text-left" data-testid={`archive-year-profit-${index}`}>
                        <p className="text-[10px] uppercase text-slate-400">Bénéfice</p>
                        <p className={`text-sm font-semibold flex items-center gap-1 ${profitPositive ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {profitPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                          {fmtEuro(profit)}
                        </p>
                      </div>
                      <div className="text-right md:text-left" data-testid={`archive-year-rdv-${index}`}>
                        <p className="text-[10px] uppercase text-slate-400">RDV</p>
                        <p className="text-sm font-semibold text-slate-900">{totals.appointments_count || 0}</p>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/40" data-testid={`archive-year-detail-${index}`}>
                    {loadingYear === arc.year || !detail ? (
                      <div className="py-8 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-[#065F46]" />
                      </div>
                    ) : (
                      <div className="space-y-5">
                        {/* Epurated summary tiles */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={`archive-tiles-${index}`}>
                          <StatTile
                            icon={Wallet}
                            label="CA annuel"
                            value={fmtEuro(detail?.totals?.revenue)}
                            accent="emerald"
                            testid={`archive-tile-revenue-${index}`}
                          />
                          <StatTile
                            icon={Receipt}
                            label="Charges"
                            value={fmtEuro(detail?.totals?.charges)}
                            accent="rose"
                            testid={`archive-tile-charges-${index}`}
                          />
                          <StatTile
                            icon={profitPositive ? TrendingUp : TrendingDown}
                            label="Bénéfice net"
                            value={fmtEuro(detail?.totals?.profit)}
                            accent={Number(detail?.totals?.profit || 0) >= 0 ? 'emerald' : 'rose'}
                            testid={`archive-tile-profit-${index}`}
                          />
                          <StatTile
                            icon={Star}
                            label="Ticket moyen"
                            value={fmtEuro(detail?.totals?.avg_ticket)}
                            accent="amber"
                            testid={`archive-tile-avg-${index}`}
                          />
                          <StatTile
                            icon={Calendar}
                            label="RDV"
                            value={detail?.totals?.appointments_count || 0}
                            accent="slate"
                            testid={`archive-tile-appointments-${index}`}
                          />
                          <StatTile
                            icon={Users}
                            label="Clients (année)"
                            value={detail?.totals?.unique_clients_in_year || 0}
                            accent="indigo"
                            testid={`archive-tile-clients-${index}`}
                          />
                          <StatTile
                            icon={FileText}
                            label="Factures"
                            value={`${detail?.totals?.invoices_count || 0} • ${fmtEuro(detail?.totals?.invoices_total_ttc)}`}
                            accent="slate"
                            testid={`archive-tile-invoices-${index}`}
                          />
                          <StatTile
                            icon={FileText}
                            label="Devis"
                            value={`${detail?.totals?.quotes_count || 0} • ${fmtEuro(detail?.totals?.quotes_total_ttc)}`}
                            accent="slate"
                            testid={`archive-tile-quotes-${index}`}
                          />
                        </div>

                        {detail?.totals?.best_month_label && (
                          <p className="text-xs text-slate-500" data-testid={`archive-best-month-${index}`}>
                            <Star className="inline w-3 h-3 text-amber-500 mr-1" />
                            Meilleur mois : <span className="font-semibold text-slate-700">{detail.totals.best_month_label}</span> avec {fmtEuro(detail.totals.best_month_revenue)} de CA.
                          </p>
                        )}

                        {/* Monthly chart */}
                        <MonthlyChart months={detail.months} indexId={index} />

                        {/* Summaries (top lists + distributions) */}
                        <div className="grid md:grid-cols-2 gap-3">
                          <SummaryRow
                            label="Top prestations"
                            items={detail.top_services}
                            accent="text-emerald-700"
                            empty="Aucune prestation"
                          />
                          <SummaryRow
                            label="Top clients"
                            items={detail.top_clients}
                            accent="text-emerald-700"
                            empty="Aucun client"
                          />
                          <SummaryRow
                            label="Modes de paiement (CA)"
                            items={detail.payment_methods}
                            accent="text-slate-900"
                            empty="Aucun paiement"
                          />
                          <SummaryRow
                            label="Types de charges"
                            items={detail.charge_types}
                            accent="text-rose-700"
                            empty="Aucune charge"
                          />
                          {detail.providers && detail.providers.length > 0 && (
                            <SummaryRow
                              label="Prestataires"
                              items={detail.providers}
                              accent="text-indigo-700"
                              empty="Aucun prestataire"
                            />
                          )}
                          {detail.client_types && detail.client_types.length > 0 && (
                            <SummaryRow
                              label="Types de clients"
                              items={detail.client_types}
                              accent="text-slate-900"
                              empty="Aucun"
                            />
                          )}
                        </div>

                        <p className="text-[11px] text-slate-400 italic flex items-center gap-1" data-testid={`archive-readonly-note-${index}`}>
                          <Lock className="w-3 h-3" />
                          Snapshot figé — les données affichées reflètent l’état de l’application au moment de la sauvegarde.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MonthlyChart = ({ months = [], indexId }) => {
  const maxAbsProfit = useMemo(() => {
    let m = 0;
    months.forEach((mo) => {
      const v = Math.abs(Number(mo.profit || 0));
      if (v > m) m = v;
    });
    return m;
  }, [months]);
  const [selected, setSelected] = useState(null);

  if (!months || months.length === 0) return null;
  const sel = selected !== null ? months[selected] : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Évolution mensuelle (Bénéfice)</p>
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded bg-emerald-500"></span> Positif</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded bg-rose-500"></span> Négatif</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[620px] flex items-end gap-1 h-32" data-testid={`archive-chart-${indexId}`}>
          {months.map((month, i) => {
            const profit = Number(month.profit || 0);
            const height = maxAbsProfit > 0 ? Math.max((Math.abs(profit) / maxAbsProfit) * 100, 4) : 0;
            const isActive = selected === i;
            const barColor = profit >= 0 ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-rose-500 hover:bg-rose-600';
            return (
              <div key={`${month.label}-${i}`} className="flex-1 min-w-0 flex flex-col items-center gap-1" data-testid={`archive-chart-bar-wrap-${indexId}-${i}`}>
                <div className="h-full w-full flex items-end justify-center">
                  <button
                    type="button"
                    className={`w-5/6 rounded-t transition-all ${barColor} ${isActive ? 'ring-2 ring-emerald-300' : ''}`}
                    style={{ height: `${height}%` }}
                    onClick={() => setSelected(selected === i ? null : i)}
                    data-testid={`archive-chart-bar-${indexId}-${i}`}
                    title={`${month.label} • ${fmtEuro(profit)}`}
                  />
                </div>
                <p className="text-[10px] text-slate-500">{month.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {sel && (
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid={`archive-chart-detail-${indexId}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase text-slate-500 font-medium">Détail {sel.label}</p>
            <p className="text-[11px] text-slate-400">
              CA {fmtEuro(sel.revenue)} • Charges {fmtEuro(sel.charges)} • Bénéfice <span className={Number(sel.profit) >= 0 ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>{fmtEuro(sel.profit)}</span>
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] uppercase text-slate-400 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> RDV ({(sel.appointments || []).length})</p>
              {(sel.appointments || []).slice(0, 8).map((apt, i) => (
                <div key={`${apt.id}-${i}`} className="text-xs flex items-center justify-between border-b border-slate-100 py-1">
                  <span className="truncate text-slate-700">{apt.client_name} • {apt.service_type}</span>
                  <span className="font-mono text-slate-900">{fmtEuro(apt.price)}</span>
                </div>
              ))}
              {(sel.appointments || []).length === 0 && <p className="text-xs text-slate-400 italic">Aucun RDV</p>}
            </div>
            <div>
              <p className="text-[11px] uppercase text-slate-400 mb-1 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Charges ({(sel.charges_items || []).length})</p>
              {(sel.charges_items || []).slice(0, 8).map((c, i) => (
                <div key={`${c.id}-${i}`} className="text-xs flex items-center justify-between border-b border-slate-100 py-1">
                  <span className="truncate text-slate-700">{c.description}</span>
                  <span className="font-mono text-slate-900">{fmtEuro(c.amount)}</span>
                </div>
              ))}
              {(sel.charges_items || []).length === 0 && <p className="text-xs text-slate-400 italic">Aucune charge</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
